import { createAsyncThunk } from '@reduxjs/toolkit';
import { FetchImagesDocumentsParams, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { AppDispatch } from '@store/types';
import { getImageContextKeyForMultipleImages } from '@v2/components/imageHandling/helpers';
import { DOCUMENT_TYPE_TAG_KEY_MAP } from '@components/imageHandling/common';
import { retrieveReferencedDocuments } from './retrieveReferencedDocuments';
import { parseAndAddUrlsToImagesDocuments } from './parseAndAddUrlsToImagesDocuments';
import { ImageHandlingState } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { isExpiredTimestamp } from '@helpers';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME } from '@v2/components/imageHandling/constants';

export const fetchImagesDocuments = createAsyncThunk<
  {
    key: string;
    data: ImageDocumentStoreItem[] | null;
    cached: boolean;
    fetchContext?: { retrieveAllDocuments: boolean; forceRetrieveFromSource: boolean };
  },
  FetchImagesDocumentsParams,
  { dispatch: AppDispatch; state: { imageHandlingV2: ImageHandlingState } }
>('imageHandlingV2/fetchImagesDocuments', async (params, { dispatch, getState }) => {
  const { scopeId, scopeReferenceId, retrieveAllDocuments = true, ...rest } = params;

  // Calculate forceRetrieveFromSource
  const isForceRetrieveFromSourceExpired = await isExpiredTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);
  const forceRetrieveFromSource = !isForceRetrieveFromSourceExpired;

  // Create current fetchContext
  const currentFetchContext = {
    retrieveAllDocuments,
    forceRetrieveFromSource,
  };

  // Generate a unique key for this request
  const key = getImageContextKeyForMultipleImages({ scopeId, scopeReferenceId });

  const state = getState();
  const documentStore = state.imageHandlingV2.imagesDocumentsContexts[key];

  // Check cache with fetchContext validation
  if (documentStore?.documentIds?.length && documentStore.ttl) {
    const isTimeValid = Date.now() < documentStore.ttl;
    const isContextSame =
      documentStore.fetchContext &&
      documentStore.fetchContext.retrieveAllDocuments === currentFetchContext.retrieveAllDocuments &&
      documentStore.fetchContext.forceRetrieveFromSource === currentFetchContext.forceRetrieveFromSource;

    if (isTimeValid && isContextSame) {
      // Get documents from dictionary
      const documents = documentStore.documentIds
        .map((docId) => state.imageHandlingV2.imagesDocumentsDict[docId])
        .filter(Boolean);

      return { key, data: documents, cached: true, fetchContext: currentFetchContext };
    }
  }

  // Find documentKey from scopeId
  const documentKey = Object.entries(DOCUMENT_TYPE_TAG_KEY_MAP).find(([, value]) => String(value) === String(scopeId))?.[0];

  if (!documentKey || !scopeReferenceId) {
    return { key, data: null, cached: false, fetchContext: currentFetchContext };
  }

  // Use retrieveReferencedDocuments with current context
  const result = await dispatch(
    retrieveReferencedDocuments({
      documentKey,
      ids: [scopeReferenceId],
      retrieveAllDocuments,
      ...rest,
    })
  );

  if (retrieveReferencedDocuments.fulfilled.match(result) && result.payload?.length) {
    // Parse documents
    const parsedResult = await dispatch(
      parseAndAddUrlsToImagesDocuments({
        imagesDocuments: result.payload,
      })
    );

    if (parseAndAddUrlsToImagesDocuments.fulfilled.match(parsedResult)) {
      // Message processing will be handled by middleware after store update
      return { key, data: parsedResult.payload, cached: false, fetchContext: currentFetchContext };
    }
  }

  return { key, data: null, cached: false, fetchContext: currentFetchContext };
});
